"use client";

import type { GPOnboardingStepProps } from "@/components/NewGamePage/GPOnboarding/utils";
import { ActionItem } from "@/components/NewGamePage/GPOnboardingLinkTree/ActionItem";
import { ASSETS } from "@/components/NewGamePage/GPOnboardingLinkTree/SharedComponents";
import { SteamLogo } from "@/components/icons/SteamLogo";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useGameOnboardingContext } from "@/context/GameOnboardingContext";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const OnboardingLinkTree = ({
  onNext = () => {},
  isVisible = true,
  track = () => {},
}: Partial<GPOnboardingStepProps> & { isVisible?: boolean } = {}) => {
  const router = useRouter();
  const { gameData, allQuests } = useGameDetailsContext();
  const { isLoggedIn, loginDirectly } = usePassportProvider();
  const { startSocialQuest, validateSocialQuest, questState } =
    useGameOnboardingContext();
  const [showLinkSteam, setShowLinkSteam] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginWindowRef, setLoginWindowRef] = useState<Window | null>(null);
  // Track if we have already proceeded to prevent double redirects
  const [hasProceeded, setHasProceeded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Find Steam wishlist quest
  const steamWishlistQuest = useMemo(
    () => allQuests?.find((quest) => quest.type === "steam_wishlist"),
    [allQuests],
  );

  // Action completion states
  const [isWishlistComplete, setIsWishlistComplete] = useState(false);
  const [isFollowXComplete, setIsFollowXComplete] = useState(false);
  const isLoginComplete = !!isLoggedIn;

  const isClaimUnlocked =
    isWishlistComplete || isFollowXComplete || isLoginComplete;

  // Debug logging and force video reload
  useEffect(() => {
    const videoSrc = isClaimUnlocked
      ? "/assets/onboarding/golden-button.mp4"
      : "/assets/onboarding/darken-golden-button.mp4";

    console.log("Claim button state:", {
      isClaimUnlocked,
      isWishlistComplete,
      isFollowXComplete,
      isLoginComplete,
      videoSrc,
    });

    // Force video to reload when source changes
    if (videoRef.current) {
      videoRef.current.load();
      console.log("Video reloaded, current src:", videoRef.current.currentSrc);
    }
  }, [isClaimUnlocked, isWishlistComplete, isFollowXComplete, isLoginComplete]);

  // Steam App ID for 8 Ball Pool
  const STEAM_APP_ID = 3760100;
  const STEAM_WISHLIST_URL = `https://store.steampowered.com/app/${STEAM_APP_ID}/`;

  const handleWishlistClick = () => {
    if (isWishlistComplete) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "steam-wishlist",
        controlType: ControlType.BUTTON,
        controlLabel: "Wishlist on Steam",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    // Open Steam URL directly (same as WishlistSteam component behavior)
    window.open(STEAM_WISHLIST_URL, "_blank");

    // Optimistically mark as complete after delay
    setTimeout(() => {
      setIsWishlistComplete(true);
    }, 1000);
  };

  const handleFollowXClick = () => {
    if (isFollowXComplete) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "follow-x",
        controlType: ControlType.BUTTON,
        controlLabel: "Follow on X",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    // Simply open X page in new tab
    window.open("https://twitter.com/Immutable", "_blank");

    // Optimistically mark as complete after delay
    setTimeout(() => {
      setIsFollowXComplete(true);
    }, 1000);
  };

  const handleLoginClick = async () => {
    if (isLoggedIn) return;

    // Simply log in - no redirect, no modal flow switching
    // Just log in and show the Claim button
    await loginDirectly("google");
  };

  // Hide login prompt after successful login - just show the Claim button
  useEffect(() => {
    if (isLoggedIn && showLogin) {
      setShowLogin(false);
    }
  }, [isLoggedIn, showLogin]);

  // Handle Steam linking
  const handleLinkSteamClick = useCallback(() => {
    if (!steamWishlistQuest || !gameData) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "link-steam",
        controlType: ControlType.BUTTON,
        controlLabel: "Link Steam to verify",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    startSocialQuest(gameData, steamWishlistQuest);
  }, [steamWishlistQuest, gameData, startSocialQuest, track]);

  // Check if Steam is connected and redirect to landing page
  // Only proceeds when Steam is actually connected (questState?.state === "connected")
  useEffect(() => {
    if (!showLinkSteam || hasProceeded) return;

    if (
      questState?.quest.type === "steam_wishlist" &&
      questState?.state === "connected"
    ) {
      setHasProceeded(true);
      setIsWishlistComplete(true);
      // Redirect to landing page immediately after successful Steam linking
      // Use window.location to ensure immediate navigation and prevent modal flow switch
      window.location.href = "/games/8-ball/";
    }
  }, [questState, showLinkSteam, hasProceeded]);

  return (
    <div className="relative w-full bg-[#131313] grow text-white overflow-hidden md:overflow-visible font-suisse">
      {/* Background Layers */}
      <div className="absolute inset-0 z-0 md:overflow-visible">
        {/* Color Theme Background */}
        <div className="absolute inset-0">
          <img
            src={ASSETS.imgHybridRewards}
            alt=""
            className="absolute h-full w-full object-cover object-[65%_center]"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        {/* Link Tree Background (transparent overlay) */}
        <div className="absolute inset-0 overflow-visible -top-[20%] h-[120%]">
          <img
            src={ASSETS.imgLinkTreeBg}
            alt=""
            className="absolute h-full w-full object-contain scale-[125%] origin-top"
          />
        </div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-0 flex flex-col h-full max-w-md mx-auto px-5 py-4 justify-end">
        {/* Top spacer: 15% on < 380px, 20% on sm, 30% on md and above */}
        {/* Group 1: Logo + Heading - positioned right below 25% spacer */}
        <div className="flex flex-col items-center flex-shrink-0">
          <Image
            src={ASSETS.imgLogo8Ball}
            className="object-contain"
            alt="Logo"
            width={300}
            height={140}
          />
          <h1 className="text-[26px] [@media(min-width:380px)]:text-[32px] md:text-[24px] [@media(min-width:380px)]:leading-10 md:leading-7 font-bold text-center text-white">
            $60k worth of prizes <br /> up for grabs!
          </h1>
        </div>

        {/* Middle section - Group 2 positioned higher */}
        <div className="flex flex-col">
          {/* Top spacer - smaller to push Group 2 up */}

          {/* Divider */}
          <div className="flex items-center gap-4 sm:gap-5 md:gap-6 my-3 sm:my-4 md:my-5">
            <div className="h-px flex-1 bg-[#fafafa]/20" />
            <span className="text-sm sm:text-base md:text-sm text-[#fafafa]">
              Choose how to get started
            </span>
            <div className="h-px flex-1 bg-[#fafafa]/20" />
          </div>

          {/* Action List */}
          <div className="flex flex-col gap-1.5">
            <ActionItem
              title="Wishlist on Steam"
              isCompleted={isWishlistComplete}
              rewardLabel="2x Tickets"
              onClick={handleWishlistClick}
              icon={
                <SteamLogo className="size-5 [@media(min-width:400px)]:size-6 sm:size-6 md:size-4 text-white" />
              }
            />
            <ActionItem
              title="Follow on X"
              isCompleted={isFollowXComplete}
              rewardLabel="2x Tickets"
              onClick={handleFollowXClick}
              icon={
                <Image
                  src="/assets/onboarding/x-icon.webp"
                  alt="X"
                  width={40}
                  height={40}
                  className="size-9 [@media(min-width:400px)]:size-10 sm:size-10 md:size-7"
                />
              }
            />
            <ActionItem
              title="Follow on Immutable"
              isCompleted={isLoginComplete}
              rewardLabel="Bonus Ticket"
              onClick={() => {
                track({
                  action: Action.PRESSED,
                  extras: {
                    control: "follow-immutable",
                    controlType: ControlType.BUTTON,
                    controlLabel: "Follow on Immutable",
                    section: GamePageSection.ONBOARDING_MODAL,
                  },
                });
                handleLoginClick();
              }}
              icon={
                <Image
                  src="/assets/onboarding/immutable-logo.webp"
                  alt="Immutable"
                  width={40}
                  height={40}
                  className="size-9 [@media(min-width:400px)]:size-10 sm:size-10 md:size-7"
                />
              }
            />
          </div>
        </div>

        {/* Group 3: Claim Button + Winners Ticker - directly on top of 10% bottom spacer */}
        <div className="flex flex-col flex-shrink-0 mt-4">
          {/* Claim Button, Link Steam Button, or Login Button */}
          {showLinkSteam ? (
            <div className="flex flex-col gap-3 items-center w-full">
              {/* Instruction text */}
              <p className="text-[12px] text-[#b6b6b6] font-medium text-center whitespace-pre">
                In order to verify your entry, please link your Steam account.
              </p>

              {/* Link Steam Button - only show if not already connected */}
              {questState?.state !== "connected" && (
                <button
                  type="button"
                  onClick={handleLinkSteamClick}
                  className="relative w-full h-[48px] bg-[#f7f7f7] text-[#0d0d0d] rounded-[26px] px-6 py-2 flex items-center justify-center gap-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] font-bold text-[18px] leading-[28px] transition-transform active:scale-[0.98]"
                >
                  Link Steam to verify
                </button>
              )}
            </div>
          ) : showLogin ? (
            <div className="flex flex-col gap-3 items-center w-full">
              {/* Instruction text */}
              <p className="text-[12px] text-[#b6b6b6] font-medium text-center whitespace-pre">
                Please log in to verify and claim your rewards.
              </p>

              {/* Log in to verify and claim Button */}
              <button
                type="button"
                onClick={() => {
                  track({
                    action: Action.PRESSED,
                    extras: {
                      control: "login-to-verify",
                      controlType: ControlType.BUTTON,
                      controlLabel: "Log in to verify and claim",
                      section: GamePageSection.ONBOARDING_MODAL,
                    },
                  });
                  handleLoginClick();
                }}
                className="relative w-full h-[48px] bg-[#f7f7f7] text-[#0d0d0d] rounded-[26px] px-6 py-2 flex items-center justify-center gap-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] font-bold text-[18px] leading-[28px] transition-transform active:scale-[0.98]"
              >
                Log in to verify and claim
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                track({
                  action: Action.PRESSED,
                  extras: {
                    control: "claim-rewards",
                    controlType: ControlType.BUTTON,
                    controlLabel: isClaimUnlocked
                      ? "Claim your rewards"
                      : "Complete at least one to unlock",
                    section: GamePageSection.ONBOARDING_MODAL,
                  },
                });

                if (!isLoginComplete) {
                  // Show login button if not logged in
                  setShowLogin(true);
                } else {
                  // If logged in, go directly to landing page - no modal flow, no conditions
                  window.location.href = "/games/8-ball/";
                }
              }}
              disabled={!isClaimUnlocked}
              className="relative w-full mx-auto h-[75px] [@media(max-width:360px)]:h-[65px] md:h-[65px] rounded-[74px] overflow-visible group transition-all"
            >
              {/* Background Layer - Video */}
              <div className="absolute overflow-hidden rounded-[74px] inset-0">
                <video
                  ref={videoRef}
                  key={isClaimUnlocked ? "golden" : "darken"}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={cn(
                    "absolute inset-0 w-full h-full rounded-[74px]",
                    isClaimUnlocked ? "object-cover scale-150" : "object-cover",
                  )}
                >
                  <source
                    src={
                      isClaimUnlocked
                        ? "/assets/onboarding/golden-button.mp4"
                        : "/assets/onboarding/darken-golden-button.mp4"
                    }
                    type="video/mp4"
                  />
                </video>
                {/* Subtle dark overlay for golden video to make text stand out */}
                {isClaimUnlocked && (
                  <div className="absolute inset-0 bg-black/10 rounded-[74px]" />
                )}
              </div>

              {/* Darken overlay - only show when button is disabled */}
              {!isClaimUnlocked && (
                <div className="absolute h-[95px] [@media(max-width:360px)]:h-[82px] md:h-[82px] w-[289px] [@media(max-width:360px)]:w-[250px] md:w-[250px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] pointer-events-none opacity-50">
                  <img
                    src={ASSETS.imgDarkenBg}
                    className="block max-w-none size-full"
                    alt=""
                  />
                </div>
              )}

              {/* Text - Exact Figma styling */}
              <span
                className={cn(
                  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[3] pointer-events-none",
                  "font-suisse font-medium text-[20px] [@media(max-width:360px)]:text-[16px] md:text-[18px] leading-[28px] [@media(max-width:360px)]:leading-[22px] md:leading-[25px] text-center text-nowrap text-white whitespace-pre",
                  "[text-shadow:#683f23_2px_2px_0px]",
                  !isClaimUnlocked && "opacity-60",
                )}
              >
                {isClaimUnlocked
                  ? "Claim your rewards"
                  : "Complete at least one to unlock"}
              </span>
            </button>
          )}

          {/* Winners Ticker */}
          <div className="mt-3 flex items-center justify-center gap-2 px-5 overflow-hidden">
            <span className="text-[12px] [@media(max-width:360px)]:text-[10px] text-[#b6b6b6] font-medium whitespace-nowrap">
              Prizes won this week
            </span>
            <div className="text-[12px] [@media(max-width:360px)]:text-[10px] text-[#fafafa] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
              Xbox, PlayStation 5, iPhone 17
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
